class HappyclothsProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.dataset.hcLoaded === 'true') return;
    this.dataset.hcLoaded = 'true';

    if (this.dataset.recommendationsPerformed === 'true' && this.querySelector('.resource-list__item')) {
      return;
    }

    this.#hideSkeletons();
    this.#loadProducts();
  }

  #hideSkeletons() {
    this.querySelectorAll('.product-recommendations__skeleton-item').forEach((item) => {
      item.style.display = 'none';
    });
  }

  #getLimit() {
    try {
      const url = new URL(this.dataset.url || '/recommendations/products?limit=4', window.location.origin);
      return Math.max(1, Math.min(8, Number(url.searchParams.get('limit')) || 4));
    } catch (_error) {
      return 4;
    }
  }

  async #loadProducts() {
    const productId = this.dataset.productId;
    const limit = this.#getLimit();

    if (!productId) {
      this.classList.add('hidden');
      return;
    }

    let products = [];

    try {
      const baseUrl = new URL(this.dataset.url || '/recommendations/products', window.location.origin);
      if (!baseUrl.pathname.endsWith('.json')) {
        baseUrl.pathname = baseUrl.pathname.replace(/\/$/, '') + '.json';
      }
      baseUrl.searchParams.set('product_id', productId);
      baseUrl.searchParams.set('limit', String(limit));
      baseUrl.searchParams.set('intent', this.dataset.intent || 'related');

      const response = await fetch(baseUrl.toString(), {
        headers: { Accept: 'application/json' },
      });

      if (response.ok) {
        const data = await response.json();
        products = Array.isArray(data.products) ? data.products : [];
      }
    } catch (error) {
      console.warn('Happycloths related products primary request failed', error);
    }

    if (!products.length) {
      try {
        const fallbackResponse = await fetch('/products.json?limit=20', {
          headers: { Accept: 'application/json' },
        });
        if (fallbackResponse.ok) {
          const fallbackData = await fallbackResponse.json();
          products = (fallbackData.products || [])
            .filter((item) => String(item.id) !== String(productId))
            .slice(0, limit);
        }
      } catch (error) {
        console.warn('Happycloths related products fallback request failed', error);
      }
    }

    products = products
      .filter((item) => String(item.id) !== String(productId))
      .slice(0, limit);

    if (!products.length) {
      this.classList.add('hidden');
      return;
    }

    this.#renderProducts(products);
  }

  #renderProducts(products) {
    this.#ensureStyles();

    let list = this.querySelector('.resource-list');
    const content = this.querySelector('.section-resource-list__content') || this;

    if (!list) {
      list = document.createElement('div');
      content.appendChild(list);
    }

    list.className = 'resource-list resource-list--grid hc-related-products-grid';
    list.innerHTML = '';

    products.forEach((product) => {
      const item = document.createElement('div');
      item.className = 'resource-list__item';

      const link = document.createElement('a');
      link.className = 'hc-related-card';
      link.href = product.url || (product.handle ? `/products/${product.handle}` : '#');
      link.setAttribute('aria-label', product.title || 'View product');

      const media = document.createElement('div');
      media.className = 'hc-related-card__media';

      const imageUrl = this.#getImageUrl(product);
      if (imageUrl) {
        const img = document.createElement('img');
        img.src = imageUrl;
        img.alt = product.title || '';
        img.loading = 'lazy';
        img.decoding = 'async';
        media.appendChild(img);
      }

      const variant = (product.variants || []).find((v) => v.available) || (product.variants || [])[0] || {};
      const priceRaw = product.price ?? product.price_min ?? variant.price;
      const compareRaw = product.compare_at_price ?? product.compare_at_price_max ?? variant.compare_at_price;
      const price = this.#numericMoney(priceRaw);
      const compare = this.#numericMoney(compareRaw);

      if (compare !== null && price !== null && compare > price) {
        const badge = document.createElement('span');
        badge.className = 'hc-related-card__badge';
        badge.textContent = 'SALE';
        media.appendChild(badge);
      }

      const info = document.createElement('div');
      info.className = 'hc-related-card__info';

      const title = document.createElement('div');
      title.className = 'hc-related-card__title';
      title.textContent = product.title || '';
      info.appendChild(title);

      if (price !== null) {
        const prices = document.createElement('div');
        prices.className = 'hc-related-card__prices';

        const currentPrice = document.createElement('span');
        currentPrice.className = 'hc-related-card__price';
        currentPrice.textContent = this.#formatMoney(price);
        prices.appendChild(currentPrice);

        if (compare !== null && compare > price) {
          const oldPrice = document.createElement('s');
          oldPrice.className = 'hc-related-card__compare';
          oldPrice.textContent = this.#formatMoney(compare);
          prices.appendChild(oldPrice);
        }

        info.appendChild(prices);
      }

      link.appendChild(media);
      link.appendChild(info);
      item.appendChild(link);
      list.appendChild(item);
    });

    this.dataset.recommendationsPerformed = 'true';
  }

  #getImageUrl(product) {
    const image = product.featured_image || (product.images || [])[0];
    let url = '';

    if (typeof image === 'string') {
      url = image;
    } else if (image && typeof image === 'object') {
      url = image.url || image.src || '';
    }

    if (url.startsWith('//')) return `https:${url}`;
    return url;
  }

  #numericMoney(value) {
    if (value === null || value === undefined || value === '') return null;
    const number = Number(value);
    if (!Number.isFinite(number)) return null;

    if (typeof value === 'string' && value.includes('.')) {
      return number;
    }

    return number / 100;
  }

  #formatMoney(amount) {
    const currency = window.Shopify?.currency?.active || 'INR';
    try {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      }).format(amount);
    } catch (_error) {
      return `₹${Math.round(amount).toLocaleString('en-IN')}`;
    }
  }

  #ensureStyles() {
    if (document.getElementById('hc-related-products-fix-styles')) return;

    const style = document.createElement('style');
    style.id = 'hc-related-products-fix-styles';
    style.textContent = `
      product-recommendations .hc-related-products-grid {
        display: grid !important;
      }

      product-recommendations .hc-related-card {
        display: block;
        height: 100%;
        overflow: hidden;
        border: 1px solid rgba(255,255,255,.12);
        border-radius: 14px;
        background: #0d0d0d;
        color: #fff;
        text-decoration: none;
        transition: transform .2s ease, border-color .2s ease;
      }

      product-recommendations .hc-related-card:hover {
        transform: translateY(-2px);
        border-color: rgba(224,0,0,.75);
      }

      product-recommendations .hc-related-card__media {
        position: relative;
        width: 100%;
        aspect-ratio: 4 / 5;
        overflow: hidden;
        background: #151515;
      }

      product-recommendations .hc-related-card__media img {
        width: 100%;
        height: 100%;
        display: block;
        object-fit: cover;
      }

      product-recommendations .hc-related-card__badge {
        position: absolute;
        top: 10px;
        left: 10px;
        z-index: 2;
        padding: 5px 8px;
        border-radius: 999px;
        background: #e00000;
        color: #fff;
        font-size: 10px;
        font-weight: 800;
        letter-spacing: .08em;
      }

      product-recommendations .hc-related-card__info {
        padding: 13px 13px 15px;
      }

      product-recommendations .hc-related-card__title {
        min-height: 2.7em;
        display: -webkit-box;
        overflow: hidden;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        font-size: 14px;
        line-height: 1.35;
        font-weight: 750;
        text-transform: uppercase;
      }

      product-recommendations .hc-related-card__prices {
        display: flex;
        align-items: baseline;
        gap: 8px;
        margin-top: 8px;
        font-size: 14px;
      }

      product-recommendations .hc-related-card__price {
        color: #fff;
        font-weight: 800;
      }

      product-recommendations .hc-related-card__compare {
        color: rgba(255,255,255,.5);
        font-size: 12px;
      }

      @media screen and (max-width: 749px) {
        product-recommendations .hc-related-card {
          border-radius: 12px;
        }

        product-recommendations .hc-related-card__info {
          padding: 10px 10px 12px;
        }

        product-recommendations .hc-related-card__title,
        product-recommendations .hc-related-card__prices {
          font-size: 12px;
        }

        product-recommendations .hc-related-card__compare {
          font-size: 11px;
        }
      }
    `;

    document.head.appendChild(style);
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', HappyclothsProductRecommendations);
}
